<template>
  <!-- AppointmentFeedbackScreen do app. Com a avaliação já feita, mostra o que foi respondido (só leitura) -->
  <div class="feedback text-primary" id="feedback">
    <h1 v-if="showTitle" class="text-pagesubtitle feedback-title">Como foi sua sessão</h1>

    <AppSection title="Teve algum problema técnico?" class="pt-0">
      <AppYesNo :modelValue="answers.trouble" :disabled="readonly" idPrefix="feedback-trouble" @update:modelValue="handleTrouble" />
      <p v-if="readonly" class="text-paragraph feedback-text">{{ rating?.description }}</p>
      <input
        v-else
        v-model.trim="descriptionModel.$model"
        class="feedback-input text-paragraph"
        id="feedback-description"
        type="text"
        :maxlength="200"
        placeholder="Gostaria de fazer algum comentário?"
      />
    </AppSection>

    <AppSection v-if="answers.trouble" title="Conseguiu realizar a sessão?">
      <AppYesNo :modelValue="answers.call_completed" :disabled="readonly" idPrefix="feedback-completed" @update:modelValue="handleCallCompleted" />
    </AppSection>

    <div v-if="answers.call_completed || !answers.trouble" class="app-card text-center feedback-card">
      <img :src="appointment?.psychologist?.formatted_profile_image" alt="" class="rounded-circle feedback-avatar" />
      <p class="text-header feedback-card-title">
        Deixe um depoimento para<br />{{ appointment?.psychologist?.name }}
      </p>
      <p class="text-label text-primary50 m-0">CRP: {{ appointment?.psychologist?.crp }}</p>
      <hr class="app-divider feedback-card-divider" />
      <p class="text-subtitle text-start m-0">Gostaria de deixar um depoimento?</p>
      <p v-if="readonly" class="text-paragraph text-start feedback-text">{{ rating?.testimonial || "N/A" }}</p>
      <input
        v-else
        v-model.trim="testimonialModel.$model"
        class="feedback-input text-paragraph"
        id="feedback-testimonial"
        type="text"
        :maxlength="200"
        placeholder="Descreva aqui"
      />
    </div>

    <div v-if="!readonly" class="feedback-actions">
      <FormButton
        app
        id="feedback-submit"
        label="Enviar avaliação"
        :disabled="!meta.valid"
        :form="silentErrors"
        :loading="submitting"
        :marginVertical="false"
        @onClick="handleSendFeedback"
      />
      <button type="button" class="btn-app btn-app-outline" id="feedback-skip" @click="emit('onRatingFinished')">Pular</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import AppSection from "@/components/app/AppSection.vue";
import AppYesNo from "@/components/app/AppYesNo.vue";
import { useSilentErrors, useVeeField } from "@/composables/useVeeField";
import { ratingRepo } from "@/di/repositories";
import { feedbackSchema, toCallRatingBody } from "@/schemas/feedback";
import { EventBus, EventBusNames } from "@/utils/EventBus";

// `showTitle`: no fim da chamada a tela é cheia; no detalhe da sessão o título fica no modal
const props = withDefaults(defineProps<{ appointment?: any; showTitle?: boolean }>(), { showTitle: false });
const emit = defineEmits<{ onRatingFinished: [] }>();

const rating = computed(() => props.appointment?.my_rating);
const readonly = computed(() => !!rating.value);

const { handleSubmit, meta, values, setFieldValue } = useForm({
  validationSchema: toTypedSchema(feedbackSchema),
  initialValues: { trouble: false, description: "", call_completed: true, testimonial: "" },
});
useVeeField<boolean>("trouble");
useVeeField<boolean>("call_completed");
const descriptionModel = useVeeField("description");
const testimonialModel = useVeeField("testimonial");
const silentErrors = useSilentErrors(feedbackSchema, values);

// respostas mostradas: as da avaliação feita ou as do formulário
const answers = computed(() =>
  readonly.value
    ? { trouble: !!rating.value?.trouble, call_completed: !!rating.value?.call_completed }
    : { trouble: !!values.trouble, call_completed: !!values.call_completed },
);
const handleTrouble = (trouble: boolean) => !readonly.value && setFieldValue("trouble", trouble);
const handleCallCompleted = (completed: boolean) => !readonly.value && setFieldValue("call_completed", completed);

const submitting = ref(false);
const handleSendFeedback = handleSubmit(async (v) => {
  if (!props.appointment) return;
  try {
    submitting.value = true;
    const res: any = await ratingRepo.createCallRating(toCallRatingBody(v, props.appointment.id) as any);
    if (res?.status !== false) {
      emit("onRatingFinished");
      EventBus.$emit(EventBusNames.REFRESH_APPOINTMENT);
      EventBus.$emit(EventBusNames.SHOW_TOAST, {
        title: "Depoimento enviado",
        msg: "Seu depoimento foi enviada e está sendo analizada",
      });
    }
  } catch (error: any) {
    EventBus.$emit(EventBusNames.SHOW_ERROR_TOAST, { msg: error.message });
  } finally {
    submitting.value = false;
  }
});

defineExpose({ handleSendFeedback });
</script>

<style scoped lang="scss">
@import "@/assets/theme/scss/user-variables";

.feedback-title {
  margin-bottom: 20px;
}

/* campo de texto do app: sem caixa, só o texto e o placeholder */
.feedback-input {
  width: 100%;
  border: 0;
  padding: 0;
  margin-top: 16px;
  background: transparent;
  color: $primary;
  outline: none;
}

.feedback-input::placeholder {
  color: $primary50;
}

.feedback-text {
  margin: 16px 0 0;
}

.feedback-card {
  margin-top: 20px;
}

.feedback-avatar {
  width: 90px;
  height: 90px;
  object-fit: cover;
}

.feedback-card-title {
  margin: 16px 0 6px;
}

.feedback-card-divider {
  margin: 20px 0;
}

.feedback-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 30px;
}
</style>
